import * as React from 'react';
import classnames from 'classnames';
import { isNil, sortBy } from 'lodash';

import Tooltip from 'components/Tooltip';

import ChannelTopUsedRow from './ChannelTopUsedRow';

import { __t, applyTranslations } from 'utils/language';

import type { MetricValueObject } from 'types';

applyTranslations(require('./channel-top-used.i18n.json'));

type Props = {
  type: string,
  width: 'normal' | 'wide',
  maxElements: number,
  metrics: Array<MetricValueObject>
};

const MAX_VISIBLE_ROWS = 7;

class ChannelTopUsed extends React.PureComponent<Props> {
  static defaultProps = {
    width: 'normal'
  };

  renderRows(): Array<React.ReactNode> {
    const rows: Array<React.ReactNode> = [];
    const { maxElements, metrics, width } = this.props;
    // sort metrics
    const sortedMetrics = sortBy(metrics, m => -m.count);
    const max = Math.min(maxElements, metrics?.length);

    for (let index = 0; index < max; index++) {
      const metric = sortedMetrics[index];

      if (!isNil(metric)) {
        const { name, count } = metric;

        rows.push(
          <ChannelTopUsedRow
            key={`metric_${name}_${count}`}
            width={width}
            maxValue={sortedMetrics[0].count}
            name={name}
            value={count}
            isEmpty={false}
          />
        );
      } else {
        rows.push(
          <ChannelTopUsedRow
            key={`metric_placeholder_${index}`}
            width={width}
          />
        );
      }
    }

    return rows;
  }

  render(): React.ReactNode {
    const { type, width, metrics } = this.props;
    const rows = this.renderRows();
    const isEmpty = metrics && metrics.length === 0;

    const classes = classnames({
      'channel-topused': true,
      'channel-topused--empty': isEmpty,
      [`channel-topused--width-${width}`]: width,
      'channel-topused--with-shadow': rows.length > MAX_VISIBLE_ROWS
    });

    if (isEmpty) {
      return (
        <section
          className={classes}
          data-qa="channel-top-used"
        >
          <div className="channel-top-used__empty">
            {__t(`channel_top_used.${type}_empty`)}
          </div>
        </section>
      );
    }

    return (
      <section
        className={classes}
        data-qa="channel-top-used"
      >
        <div className="channel-top-used__wrapper">
          <div className="channel-topused__title">
            <Tooltip message={__t(`channel_top_used.${type}_tooltip`)}>
              {__t(`channel_top_used.${type}_title`)}
            </Tooltip>
          </div>
          <div className="channel-topused__subtitle">
            {__t(`channel_top_used.${type}_subtitle`)}
          </div>
          <div className="channel-topused__container">
            {rows}
          </div>
        </div>
      </section>
    );
  }
}

export default ChannelTopUsed;
